import { t } from '@/utils/i18n'
import { getApiLang } from '@/utils/i18n'
import 'react'
import axios from 'axios'
import type { InternalAxiosRequestConfig, AxiosRequestHeaders } from 'axios'
import { useRequest } from 'ahooks'
import { Toast } from 'antd-mobile'
import { getConfig } from '@/config/config'
import constant from '@/constant'
import utils from '@/utils/utils'
import storageUtil from '@/utils/localStorage'
import common from '@/utils/common'
import router from '@/router'
import { routesMap } from '@/router/routes'
import type { UseRequestHttpResult, AxiosConfig } from './types'

const conf = getConfig()
const { getUuid, isExist, onlyLastRequest, to, getQuerySearch } = utils
const { getLocalStorage } = storageUtil
const {
  errCode: {
    SMALL_TOKEN_HAS_EXPIRED,
    BIG_TOKEN_HAS_EXPIRED,
    TOKEN_IS_EMPTY,
    TOKEN_IS_ILLEGAL,
    TMP_TOKEN_HAS_EXPIRED,
  },
} = constant

// 基础的请求选项
const baseRequestOptions = {
  manual: true,
  showMessage: true,
}

// 防止重复弹窗的变量
let showTokenExpiredDialog = false

// 创建请求实例，请求拦截
const instance = axios.create()
instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (!config) {
    config = {} as InternalAxiosRequestConfig
  }

  if (!config.headers) {
    config.headers = {} as AxiosRequestHeaders
  }

  config.baseURL = getLocalStorage('apiUrl') || conf.rootPath
  
  // 优先取临时登录的tempToken（关闭网页标签或关闭app webview会清除）
  // 再取本地存储的token
  const token = sessionStorage.getItem('tempToken') || getLocalStorage('token') || ''

  if (config.url?.includes('/oss/upload')) {
    Object.assign(config.headers, {
      'kp-token': token,
      'kp-region': getLocalStorage('region') || 'VN',
      'kp-request-id': getUuid(),
      'kp-uuid': getLocalStorage('uuid') || '',
      'kp-source': conf.source,
      'kp-corporation-id': getLocalStorage('site') || '',
      'Content-Type': 'multipart/form-data;',
    })
  } else {
    Object.assign(config.headers, {
      'kp-token': token,
      'kp-region': getLocalStorage('region') || 'VN',
      'Content-Type': 'application/json; charset=utf-8',
      'kp-source': conf.source,
      'kp-request-id': getUuid(),
      'kp-uuid': getLocalStorage('uuid') || '',
      'kp-version': conf.version,
      'kp-language': getApiLang(),
      'kp-device-name': '',
      'kp-corporation-id': getLocalStorage('site') || '',
    })
  }

  return config
})
instance.interceptors.response.use(async (res) => {
  res = res || {}
  const { data: rawData = {} } = res
  const config: AxiosConfig = res.config
  const { code } = rawData
  const corpInfo = getLocalStorage('corpInfo', true)
  const tempToken = sessionStorage.getItem('tempToken')
  const { corpId } = corpInfo || {}

  // “token大票过期”或“token为空”或“token非法”或“临时token过期”时，重新登录
  if (
    [
      BIG_TOKEN_HAS_EXPIRED,
      TOKEN_IS_EMPTY,
      TOKEN_IS_ILLEGAL,
      TMP_TOKEN_HAS_EXPIRED,
    ].includes(code)
  ) {
    // 如果是临时登录，重新临时登录
    if (tempToken && corpId) {
      await common.onTempLogin(corpId)
      location.reload()
    // 否则是正式登录，返回登录页重新登录
    } else {
      if (!showTokenExpiredDialog) {
        showTokenExpiredDialog = true
  
        setTimeout(() => {
          Toast.show({
            icon: 'fail',
            content: t('登录已过期'),
            duration: 2000,
            afterClose: () => {
              showTokenExpiredDialog = false
            },
          })
          // 跳回登录页
          const searchString = getQuerySearch()
          router.navigate(routesMap.Login + searchString)
        }, 100)
      }
    }
    return Promise.reject(rawData)
  }

  // 小票过期
  if (code === SMALL_TOKEN_HAS_EXPIRED) {
    // 临时登录，重新临时登录
    if (tempToken && corpId) {
      await common.onTempLogin(corpId)
      location.reload()
    // 正式登录，并且存在token, 续票
    } else if (getLocalStorage('token')) {
      const exchangeTokenUtil = (await import('@/utils/token')).default
      const smallToken = await exchangeTokenUtil?.exchangeSmallToken()
      if (smallToken && location?.reload && typeof location?.reload === 'function') location.reload()
    } else {
      // 跳回登录页
      const searchString = getQuerySearch()
      router.navigate(routesMap.Login + searchString)
    }
    return
  }

  // 其它错误
  if (code !== 0) {
    if (rawData.msg && rawData.requestId && NODE_ENV !== 'production') {
      rawData.msg += `\nrequestId: ${rawData.requestId}`
    }

    if (config.showMessage && rawData.msg) {
      Toast.show(rawData.msg)
    }
    return Promise.reject(rawData)
  }

  return rawData
})

/**
 * 处理列表数据，是否有下一页，是否是空列表等
 * @param useRequestData
 * @param params
 * @returns
 */
function handleExtraData(useRequestData: any, options: any = {}) {
  const { data, error } = useRequestData
  const { pageNum, pages, data: resData, extend, msg, code } = data || {}

  Object.assign(useRequestData, {
    hasNextPage: true,
    isDataEmpty: false,
    data: resData,
    extend: extend || {},
    msg: msg || (error && error.msg) || '',
  })

  if (isExist(pages) && !pages) {
    useRequestData.isDataEmpty = true
  }
  if (
    (Array.isArray(resData) && pageNum >= pages) ||
    error ||
    (code === 0 && !resData)
  ) {
    useRequestData.hasNextPage = false
  }

  // 简化runAsync返回的数据
  const runAsync = useRequestData.runAsync
  useRequestData.runAsync = async function wrapRunAsync(...args: any[]) {
    const [pageNum, pages] = args
    const [err, res] = await to(runAsync(...args))
    const DEFAULT_RESDATA = pageNum || pages ? [] : {}
    const resData = (res && res.data) || DEFAULT_RESDATA
    useRequestData.data = resData
    useRequestData.error = err

    if (err) {
      console.warn(err.msg)
    }

    return resData
  }

  if (options.isLastRequest) {
    useRequestData.run = onlyLastRequest(useRequestData.run)
    useRequestData.runAsync = onlyLastRequest(useRequestData.runAsync)
  }

  return useRequestData
}

/**
 * 处理useRequest get请求
 * @param url
 * @param data
 * @param options
 * @returns
 */
function useGet(
  url: string,
  data: object = {},
  options: object = {}
): UseRequestHttpResult {
  const totalOptions = { ...baseRequestOptions, ...options }
  let totalData = { ...data }
  const useRequestData = useRequest((newData = {}) => {
    totalData = { ...totalData, ...newData }
    return instance({
      url,
      method: 'get',
      params: totalData,
      ...totalOptions,
    })
  }, totalOptions)

  return handleExtraData(useRequestData, totalOptions)
}

/**
 * 处理useRequest post请求
 * @param url
 * @param data
 * @param options
 * @returns
 */
function usePost(
  url: string,
  data: object = {},
  options: object = {}
): UseRequestHttpResult {
  const totalOptions = { ...baseRequestOptions, ...options }
  let totalData = { ...data }
  const useRequestData = useRequest((newData = {}) => {
    totalData = { ...totalData, ...newData }
    return instance({
      url,
      method: 'post',
      data: totalData,
      ...totalOptions,
    })
  }, totalOptions)

  return handleExtraData(useRequestData, totalOptions)
}

/**
 * 普通post请求
 * @param url
 * @param data
 * @param options
 * @returns
 */
function get<T>(
  url: string,
  data: object = {},
  options: object = {}
): Promise<T> {
  const totalOptions = { ...baseRequestOptions, ...options }
  return instance({
    url,
    method: 'get',
    params: data,
    ...totalOptions,
  })
}

/**
 * 普通post请求
 * @param url
 * @param data
 * @param options
 * @returns
 */
function post<T>(
  url: string,
  data: object = {},
  options: object = {}
): Promise<T> {
  const totalOptions = { ...baseRequestOptions, ...options }

  return instance({
    url,
    method: 'post',
    data: data,
    ...totalOptions,
  })
}

// 暴露的http请求实例
const http = {
  useGet,
  usePost,
  get,
  post,
}

export default http
